import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { useSafeThemeClasses, useSafeTheme } from '@/shared/theme';
import ParchmentReveal from '@/shared/theme/fantasy/ParchmentReveal';
import { useFetchBlogs, blogApiService } from '@/features/blog';
import { useUserState } from '@/features/user/operations/UserStateProvider';
import { useComments } from '@/features/blog/operations/useComments';
import { usePublishBlog } from '@/features/blog/operations/usePublishBlog';
import { ROUTES } from '@/shared/config';
import { dateUtils, logger } from '@/shared/utils';
import { sanitizeBlogHtml } from '@/shared/utils/blogContentUtils';
import { useConfirmDialog } from '@/shared/components/ui';
import toast from 'react-hot-toast';
import type { BlogPost } from '@/features/blog/api';
import { SEO } from '@/shared/components/SEO';
import { ENV } from '@/config/environment';

// Reader URLs accept either the clean slug (new links) or a raw UUID
// (backward compat for links shared before slugs existed) in the same
// route param slot.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const BlogReader: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isPreviewMode = location.pathname.includes('/blog/preview/');
  logger.debug('BlogReader mounted', 'BlogReader', { id, isPreviewMode });
  const theme = useSafeThemeClasses();
  const { variant } = useSafeTheme();
  const { getBlogById, getBlogBySlug } = useFetchBlogs();
  const { publishBlog, loading: publishLoading } = usePublishBlog();
  const { user, isAuthenticated } = useUserState();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Comments need the blog's real internal ID, not the route param - once
  // the param can be a slug, it's the wrong value to send to the comments
  // endpoint. Wait for the post to resolve (same as the preview-mode
  // disabled-until-loaded behavior this already had).
  const { comments, loading: commentsLoading, submitting, createComment, total: commentsTotal } = useComments(
    isPreviewMode ? '' : (post?.id || '')
  );
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  
  // Check if current user is the author (only when post is loaded)
  const isAuthor = isAuthenticated && user && post && post.author?.id === user.id;
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [archiving, setArchiving] = useState(false);

  const handleArchive = async () => {
    if (!post) return;
    const ok = await confirm({
      title: 'Archive this post?',
      message: 'Archived posts are removed from public view. You can unarchive them later from Drafts.',
      confirmLabel: 'Archive',
    });
    if (!ok) return;

    try {
      setArchiving(true);
      const response = await blogApiService.archiveBlog(post.id);
      if (response.success) {
        toast.success('Blog archived');
        logger.info('Blog archived', 'BlogReader', { blogId: post.id });
        navigate(ROUTES.DRAFTS);
      } else {
        toast.error(response.error?.message || 'Failed to archive blog');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to archive blog', 'BlogReader', { error });
    } finally {
      setArchiving(false);
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      if (!id) return;
      logger.debug('Fetching post', 'BlogReader', { id, isPreviewMode });
      setIsLoading(true);
      setError(null);
      try {
        // Preview links (BLOG_PREVIEW) are always ID-based - author-only,
        // never meant to be shared publicly, no reason to churn them.
        // The public reader route accepts either a slug or a legacy UUID
        // in the same slot, so links shared before slugs existed keep working.
        const fetchedPost = isPreviewMode || UUID_RE.test(id)
          ? await getBlogById(id, { preview: isPreviewMode })
          : await getBlogBySlug(id);
        logger.debug('Post fetched successfully', 'BlogReader', { postId: fetchedPost?.id });
        setPost(fetchedPost);
        if (!fetchedPost) {
          setError(isPreviewMode ? 'Draft not found or you do not have access' : 'Post not found');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load post');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
  }, [id, getBlogById, getBlogBySlug, isPreviewMode]);

  const handlePublishFromPreview = async () => {
    if (!id) return;
    try {
      const success = await publishBlog(id);
      if (success) {
        toast.success('Blog published successfully!');
        // Slugs are set at creation and never change, so post.slug is
        // already the right public URL even though we're still viewing
        // this via the (ID-based) preview route.
        navigate(ROUTES.BLOG_POST(post?.slug || id));
      } else {
        toast.error('Failed to publish blog');
      }
    } catch (err) {
      logger.error('Failed to publish from preview', 'BlogReader', { error: err, id });
      toast.error('Failed to publish blog');
    }
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${theme.textAccent} mx-auto mb-4`}></div>
          <p className={theme.textSecondary}>Loading post...</p>
        </div>
      </div>
    );
  }
  if (error) return <div className={`min-h-screen ${theme.background} flex items-center justify-center`}><div className={`p-8 rounded-2xl ${theme.alertError}`}>{error}</div></div>;
  if (!post) return <div className={`min-h-screen ${theme.background} flex items-center justify-center`}><div className={`p-8 rounded-2xl ${theme.card}`}><p className={theme.textPrimary}>Post not found</p></div></div>;

  return (
    <div className={`min-h-screen ${theme.background} py-8 relative overflow-hidden pb-20`}>
      <SEO
        title={post.title}
        description={post.seo?.meta_description || post.excerpt}
        path={`/blog/${post.slug || post.id}`}
        image={post.featured_image_url}
        type="article"
        noIndex={!post.is_published}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.seo?.meta_description || post.excerpt,
          image: post.featured_image_url ? [post.featured_image_url] : undefined,
          datePublished: post.published_at || post.created_at,
          dateModified: post.updated_at || post.published_at || post.created_at,
          author: { '@type': 'Person', name: post.author?.full_name || post.author?.username },
          publisher: { '@type': 'Organization', name: ENV.app.name },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `${ENV.app.siteUrl}/blog/${post.slug || post.id}` },
        }}
      />
      <ConfirmDialog />
      {/* Glass theme background orbs - Animated */}
      <div className='fixed inset-0 overflow-hidden pointer-events-none'>
        <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[0]} animate-pulse`}></div>
        <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[1]} animate-pulse`} style={{ animationDelay: '1s' }}></div>
      </div>
      <article className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 animate-fadeIn`}>
      <ParchmentReveal disabled={variant !== 'fantasy'}>
        {/* Glass Tile Container - Enhanced - Toned down */}
        <div className={`${theme.card} rounded-3xl p-8 md:p-12 lg:p-16 relative overflow-hidden transition-all duration-500`}>
          {isPreviewMode && (
            <div className={`mb-8 p-4 rounded-xl flex flex-wrap items-center justify-between gap-4 ${theme.alertWarning}`}>
              <div>
                <p className={`font-semibold ${theme.textHeading}`}>Draft preview</p>
                <p className={`text-sm ${theme.textSecondary}`}>Only you can see this. Publish when ready.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link
                  to={ROUTES.DRAFTS}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${theme.buttonSecondary}`}
                >
                  Back to drafts
                </Link>
                <Link
                  to={ROUTES.BLOG_EDIT(id || '')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${theme.buttonSecondary}`}
                >
                  Edit
                </Link>
                <button
                  type="button"
                  onClick={handlePublishFromPreview}
                  disabled={publishLoading}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${theme.buttonPrimary} disabled:opacity-50`}
                >
                  {publishLoading ? 'Publishing...' : 'Publish'}
                </button>
              </div>
            </div>
          )}
          {/* Header - Animated */}
          <header className="mb-8 animate-slideUp">
            <h1 className={`text-4xl md:text-5xl font-bold mb-6 ${theme.textHeading} tracking-tight leading-tight`}>{post.title}</h1>
            
            <div className={`flex flex-wrap items-center gap-4 mb-6 ${theme.textSecondary} text-sm`}>
              {post.author?.username && (
                <span className={`flex items-center gap-2 ${theme.textPrimary}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {post.author.username}
                </span>
              )}
              {post.published_at && (
                <time dateTime={post.published_at} className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {new Date(post.published_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              )}
              {post.reading_time_minutes && (
                <span className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {post.reading_time_minutes} min read
                </span>
              )}
            </div>
          </header>

          {/* Content - Render HTML (Sanitized) - Animated */}
          <div
            className={`blog-html-content prose prose-lg max-w-none ${theme.textPrimary} animate-fadeIn`}
            style={{
              '--tw-prose-headings': 'inherit',
              '--tw-prose-body': 'inherit',
              '--tw-prose-links': 'inherit',
              '--tw-prose-bold': 'inherit',
              '--tw-prose-code': 'inherit',
              '--tw-prose-pre-bg': 'inherit',
              animationDelay: '0.2s',
            } as React.CSSProperties}
            dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(post.content) }}
          />

          {/* Tags - Animated */}
          {post.tags && post.tags.length > 0 && (
            <div className={`mt-8 pt-8 border-t border-opacity-20 animate-fadeIn`} style={{ animationDelay: '0.3s' }}>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className={`text-sm px-4 py-1.5 rounded-full font-medium transition-all duration-300 ${theme.badge} ${theme.textSecondary} hover:scale-105 hover:shadow-md`}
                    style={{ animationDelay: `${0.4 + index * 0.1}s` }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Share Buttons - removed for now, broken (see plan for later fix); no other
              part of this page depends on the removed state/handlers. */}

          {/* Author Actions - Modern */}
          {isAuthor && (
            <div className={`mt-8 pt-8 border-t border-opacity-20 animate-fadeIn`} style={{ animationDelay: '0.5s' }}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-bold ${theme.textHeading} tracking-tight`}>Author Actions</h3>
                <button
                  onClick={handleArchive}
                  disabled={archiving}
                  className={`px-5 py-2.5 rounded-xl transition-all duration-300 text-sm font-medium flex items-center gap-2 ${theme.buttonSecondary} hover:scale-105 hover:shadow-lg active:scale-95 disabled:opacity-50`}
                  title="Archive this blog post">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  {archiving ? 'Archiving...' : 'Archive'}
                </button>
              </div>
            </div>
          )}
        </div>

        {!isPreviewMode && (
        <div className={`${theme.card} rounded-3xl p-8 md:p-12 lg:p-16 mt-8 animate-fadeIn`} style={{ animationDelay: '0.5s' }}>
          <h2 className={`text-2xl font-bold mb-8 ${theme.textHeading} tracking-tight`}>
            Comments {commentsTotal > 0 && `(${commentsTotal})`}
          </h2>

          {/* Comment Form - Modern - Max 2 colors */}
          {isAuthenticated ? (
            <div className={`mb-8 p-5 rounded-2xl ${theme.input} transition-all duration-300`}>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                rows={4}
                className={`w-full p-4 rounded-xl ${theme.textPrimary} ${theme.input} focus:outline-none focus:ring-2 focus:scale-[1.01] transition-all duration-300 ${theme.textAccent} resize-none`}
              />
              <div className="flex justify-end mt-4">
                <button
                  onClick={async () => {
                    if (!newComment.trim()) {
                      toast.error('Comment cannot be empty');
                      return;
                    }
                    const result = await createComment(newComment);
                    if (result) {
                      setNewComment('');
                    }
                  }}
                  disabled={submitting || !newComment.trim()}
                  className={`px-6 py-2.5 rounded-xl transition-all duration-300 text-sm font-medium ${theme.buttonPrimary} ${theme.buttonTextPrimary} disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 hover:shadow-lg active:scale-95`}
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            </div>
          ) : (
            <div className={`mb-8 p-4 rounded-lg text-center ${theme.card}`}>
              <p className={theme.textSecondary}>
                Please <Link to={ROUTES.LOGIN} className={`${theme.textAccent} underline`}>log in</Link> to post a comment.
              </p>
            </div>
          )}

          {/* Comments List */}
          {commentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.textAccent}`}></div>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8">
              <p className={theme.textMuted}>No comments yet. Be the first to comment!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {comments.map((comment, index) => {
                const author = comment.author || { username: 'Anonymous', full_name: 'Anonymous User' };
                return (
                <div 
                  key={comment.id} 
                  className={`p-5 rounded-2xl transition-all duration-300 hover:scale-[1.01] ${theme.card} animate-fadeIn`}
                  style={{ animationDelay: `${0.6 + index * 0.1}s` }}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${theme.avatar} font-semibold`}>
                      {(author.username || 'A').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-semibold ${theme.textHeading}`}>
                          {author.full_name || author.username || 'Anonymous'}
                        </span>
                        <span className={`text-xs ${theme.textMuted}`}>
                          {dateUtils.timeAgo(new Date(comment.created_at))}
                        </span>
                      </div>
                      <p className={`${theme.textPrimary} mb-2`}>{comment.content}</p>
                      <div className="flex items-center gap-4">
                        {isAuthenticated && (
                          <button
                            onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                            className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-all duration-300 ${theme.textMuted} hover:${theme.textAccent} hover:bg-opacity-20 hover:scale-105 active:scale-95`}
                          >
                            Reply
                          </button>
                        )}
                        {comment.like_count > 0 && (
                          <span className={`text-sm ${theme.textMuted}`}>
                            {comment.like_count} like{comment.like_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      
                      {/* Reply Form - Modern - Max 2 colors */}
                      {replyingTo === comment.id && isAuthenticated && (
                        <div className={`mt-4 p-4 rounded-xl ${theme.input} transition-all duration-300`}>
                          <textarea
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            placeholder="Write a reply..."
                            rows={3}
                            className={`w-full p-3 rounded-xl ${theme.textPrimary} ${theme.input} focus:outline-none focus:ring-2 focus:scale-[1.01] transition-all duration-300 ${theme.textAccent} resize-none`}
                          />
                          <div className="flex justify-end gap-3 mt-3">
                            <button
                              onClick={() => {
                                setReplyingTo(null);
                                setReplyContent('');
                              }}
                              className={`px-5 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${theme.buttonGhost} hover:scale-105 active:scale-95`}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={async () => {
                                if (!replyContent.trim()) {
                                  toast.error('Reply cannot be empty');
                                  return;
                                }
                                const result = await createComment(replyContent, comment.id);
                                if (result) {
                                  setReplyContent('');
                                  setReplyingTo(null);
                                }
                              }}
                              disabled={submitting || !replyContent.trim()}
                              className={`px-5 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${theme.buttonPrimary} ${theme.buttonTextPrimary} disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 hover:shadow-lg active:scale-95`}
                            >
                              {submitting ? 'Posting...' : 'Post Reply'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Replies - Modern */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="mt-4 ml-8 space-y-3">
                          {comment.replies.map((reply) => {
                            const replyAuthor = reply.author || { username: 'Anonymous', full_name: 'Anonymous User' };
                            return (
                              <div key={reply.id} className={`p-4 rounded-xl transition-all duration-300 ${theme.card}`}>
                                <div className="flex items-start gap-2 mb-1">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${theme.avatar} font-semibold text-sm`}>
                                    {(replyAuthor.username || 'A').charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`font-semibold text-sm ${theme.textHeading}`}>
                                        {replyAuthor.full_name || replyAuthor.username || 'Anonymous'}
                                      </span>
                                      <span className={`text-xs ${theme.textMuted}`}>
                                        {dateUtils.timeAgo(new Date(reply.created_at))}
                                      </span>
                                    </div>
                                    <p className={`text-sm ${theme.textPrimary}`}>{reply.content}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
        )}
      </ParchmentReveal>
      </article>
    </div>
  );
};

export default BlogReader;

